import React, { useMemo, useState } from 'react';

// Base comum da Analise do Banco: cores, formatos e pecas pequenas.
//
// Cores por DONO (identidade), validadas no fundo escuro do portal (#15252B)
// com o validador da skill dataviz, na ordem em que aparecem lado a lado na
// barra de CPU (ERP, Centrao no ERP, Outro, Portais): todos os checks passam.
// Gravidade NAO usa essas cores: e icone + palavra (ver Gravidade), para um
// cartao "Alta + ERP" nao ter duas etiquetas da mesma cor.
export const ORIGEM = {
  erp: { rotulo: 'ERP (Micro)', cor: 'var(--ab-erp)' },
  centrao_erp: { rotulo: 'Centrão no ERP', cor: 'var(--ab-centrao)' },
  outro: { rotulo: 'Outro', cor: 'var(--ab-outro)' },
  portal: { rotulo: 'Nossos portais', cor: 'var(--ab-portal)' },
  misto: { rotulo: 'ERP + portal', cor: 'var(--ab-misto)' },
};
export const ORDEM_ORIGENS = ['erp', 'centrao_erp', 'outro', 'portal'];

export const ACAO = {
  micro: { rotulo: 'Microuniverso', titulo: 'Pedir à Microuniverso', icone: 'bi-building' },
  nos: { rotulo: 'Nós (TI)', titulo: 'Corrigir do nosso lado', icone: 'bi-tools' },
  ambos: { rotulo: 'Os dois lados', titulo: 'Os dois lados', icone: 'bi-arrow-left-right' },
};

export const GRAV = {
  alta: { rotulo: 'Alta', icone: 'bi-exclamation-octagon-fill', cor: 'var(--ab-st-critico)' },
  media: { rotulo: 'Média', icone: 'bi-exclamation-triangle-fill', cor: 'var(--ab-st-aviso)' },
  baixa: { rotulo: 'Baixa', icone: 'bi-info-circle-fill', cor: 'var(--portal-muted)' },
};

// ── Formatos (um so jeito de escrever cada coisa, em todas as telas) ────────

export const num = (n) => Math.round(Number(n || 0)).toLocaleString('pt-BR');
export const dec = (n, c = 1) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c });
export const pct = (parte, total) => (total ? `${dec((100 * Number(parte || 0)) / total, 1)}%` : '—');
export const media = (total, n) => (Number(n) ? Number(total) / Number(n) : 0);

// Duracao humana unica: 79 ms · 1,2 s · 15 min · 6,9 h.
export const duracao = (ms) => {
  const v = Number(ms || 0);
  if (v >= 3600000) return `${dec(v / 3600000, 1)} h`;
  if (v >= 60000) return `${num(v / 60000)} min`;
  if (v >= 1000) return `${dec(v / 1000, 1)} s`;
  return `${num(v)} ms`;
};
export const horasCpu = (ms) => `${dec(Number(ms || 0) / 3600000, 1)} h`;

// Leitura LOGICA: cada pagina de 8 KB lida na memoria conta, inclusive a mesma
// pagina relida. Por isso a tela mostra por execucao e, no total, "quantas
// vezes o banco inteiro" — nunca o total em TB/PB, que parece volume de dado.
export const bytes = (b) => {
  let v = Number(b || 0);
  for (const u of ['B', 'KB', 'MB', 'GB', 'TB', 'PB']) {
    if (v < 1024 || u === 'PB') return `${dec(v, v < 10 && u !== 'B' ? 1 : 0)} ${u}`;
    v /= 1024;
  }
  return '';
};
export const porExecucao = (paginas, execucoes) => bytes((Number(paginas || 0) * 8192) / Math.max(1, Number(execucoes || 0)));
export const vezesOBanco = (paginas, tamanhoGb) => (tamanhoGb ? (Number(paginas || 0) * 8) / 1024 / 1024 / Number(tamanhoGb) : null);

export const dataHora = (v) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
export const dia = (v) => (v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—');
export const diaIso = (v) => {
  const x = new Date(v);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
export const diaBR = (iso) => (iso ? iso.split('-').reverse().slice(0, 2).join('/') : '—');
export const diasDesde = (v) => (v ? Math.floor((Date.now() - new Date(v).getTime()) / 86400000) : null);
export const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// "de 05/10 a 26/10 · pior dia 07/10 (237) · 17h (47%) e 22h (15%)"
export function quandoTexto(q, { curto = false } = {}) {
  if (!q) return '';
  if (q.primeiro) {
    const ini = dia(q.primeiro); const fim = dia(q.ultimo);
    const partes = [ini === fim ? `em ${ini}` : `de ${ini} a ${fim}`];
    if (q.pico && q.dias > 1 && q.pico.n > 1) partes.push(`pior dia ${diaBR(q.pico.dia)} (${num(q.pico.n)})`);
    if (!curto && q.horarios?.texto) partes.push(`mais às ${q.horarios.texto}`);
    if (!curto) partes.push(`última vez ${dataHora(q.ultimo)}`);
    return partes.join(' · ');
  }
  if (q.desde) return `acumulado desde ${dia(q.desde)}`;
  return 'situação atual do servidor';
}

// ── Pecas ───────────────────────────────────────────────────────────────────

export function PillOrigem({ origem }) {
  const o = ORIGEM[origem] || ORIGEM.outro;
  return <span className="ab-pill" style={{ '--cor': o.cor }}><i className="ab-pill__ponto" />{o.rotulo}</span>;
}

export function Gravidade({ g, so = false }) {
  const x = GRAV[g] || GRAV.baixa;
  return (
    <span className="ab-grav" title={`Gravidade ${x.rotulo.toLowerCase()}`}>
      <i className={`bi ${x.icone}`} style={{ color: x.cor }} />{!so && <span>{x.rotulo}</span>}
    </span>
  );
}

export function QuemResolve({ acao }) {
  const a = ACAO[acao] || ACAO.nos;
  return <span className={`ab-quem ab-quem--${acao}`}><i className={`bi ${a.icone}`} /> {a.rotulo}</span>;
}

// Faixa de 90 dias. `max` em comum entre linhas que se comparam (uma tabela),
// senao 392 e 13 parecem igualmente altos.
export function Faixa90({ porDia, cor = 'var(--ab-erp)', dias = 90, max: maxComum, altura = 22 }) {
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const valores = Array.from({ length: dias }, (_, i) => {
    const d = new Date(hoje); d.setDate(d.getDate() - (dias - 1 - i));
    return porDia?.[diaIso(d)] || 0;
  });
  const max = Math.max(1, maxComum || 0, ...(maxComum ? [] : valores));
  // Raiz quadrada: com escala comum, um problema de 3 nao some ao lado de um de 237.
  const h = (v) => Math.max(2, (altura - 2) * Math.sqrt(v / max));
  return (
    <svg viewBox={`0 0 ${dias * 3} ${altura}`} width="100%" height={altura} preserveAspectRatio="none" aria-hidden="true">
      <line x1="0" y1={altura - 0.5} x2={dias * 3} y2={altura - 0.5} stroke="var(--portal-border)" />
      {valores.map((v, i) => (v ? <rect key={i} x={i * 3} y={altura - h(v)} width="2.2" height={h(v)} rx="0.6" fill={cor} /> : null))}
    </svg>
  );
}

export function Carregando({ texto = 'Carregando...' }) {
  return <div className="content-card--centered"><div className="screen-loader__spinner" /><p>{texto}</p></div>;
}

// Ordenacao por clique no cabecalho (desc -> asc -> sem ordem).
export function useOrdenacao(linhas, inicial = null) {
  const [ordem, setOrdem] = useState(inicial);
  const ordenadas = useMemo(() => {
    if (!ordem) return linhas;
    const { campo, dir, valor } = ordem;
    const v = valor || ((l) => l[campo]);
    return [...linhas].sort((a, b) => {
      const x = v(a); const y = v(b);
      const numerico = (typeof x === 'number' || typeof y === 'number') || (!Number.isNaN(Number(x)) && !Number.isNaN(Number(y)) && x !== '' && y !== '');
      const r = numerico ? Number(x || 0) - Number(y || 0) : String(x ?? '').localeCompare(String(y ?? ''), 'pt-BR', { numeric: true });
      return dir === 'asc' ? r : -r;
    });
  }, [linhas, ordem]);
  const alternar = (campo, valor) => setOrdem((o) => {
    if (!o || o.campo !== campo) return { campo, dir: 'desc', valor };
    if (o.dir === 'desc') return { campo, dir: 'asc', valor };
    return null;
  });
  const Th = ({ campo, valor, children, direita, titulo }) => {
    const ativo = ordem?.campo === campo;
    return (
      <th className="portal-table__sortable" style={direita ? { textAlign: 'right' } : undefined} title={titulo}
        onClick={() => alternar(campo, valor)}>
        {children}{' '}
        <i className={`bi ${ativo ? (ordem.dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up'} portal-table__sort-icon ${ativo ? 'portal-table__sort-icon--active' : ''}`} />
      </th>
    );
  };
  return { ordenadas, Th };
}

// Esperas agrupadas em familias legiveis: o fato e "o servidor espera por
// paralelismo", nao seis codigos CX* diferentes.
export const FAMILIAS_ESPERA = [
  { chave: 'paralelismo', rotulo: 'Paralelismo', explica: 'Consulta dividida entre vários núcleos, com as partes esperando umas pelas outras.', teste: (t) => /^CX|^EXECSYNC|^HTBUILD|^BMP/.test(t) },
  { chave: 'rede', rotulo: 'Programa lento para receber o resultado', explica: 'O banco já respondeu e espera o programa ler (resultado grande ou programa lento).', teste: (t) => t === 'ASYNC_NETWORK_IO' },
  { chave: 'memoria', rotulo: 'Disputa em memória', explica: 'Estruturas internas em memória disputadas (comum junto com paralelismo e tempdb).', teste: (t) => /^LATCH_|^PAGELATCH_|^RESOURCE_SEMAPHORE|^CMEMTHREAD/.test(t) },
  { chave: 'cpu', rotulo: 'Processador', explica: 'Consultas esperando a vez no processador.', teste: (t) => /^SOS_SCHEDULER_YIELD|^THREADPOOL/.test(t) },
  { chave: 'disco', rotulo: 'Disco', explica: 'Leitura de dado que não estava em memória, gravação do log, backup.', teste: (t) => /^PAGEIOLATCH_|^WRITELOG|IO_COMPLETION|^BACKUP|^LOGBUFFER/.test(t) },
  { chave: 'bloqueio', rotulo: 'Bloqueio', explica: 'Esperando outra transação liberar a linha ou a tabela.', teste: (t) => /^LCK_M_/.test(t) },
];
export const familiaDaEspera = (tipo) => FAMILIAS_ESPERA.find((f) => f.teste(tipo)) || { chave: 'outras', rotulo: 'Outras', explica: 'Demais esperas.' };
